import { animate } from 'animejs';
import React, { useEffect, useRef } from 'react';

interface AnimatedVideoProps {
  src: string;
  className?: string;
}

export const AnimatedVideo: React.FC<AnimatedVideoProps> = ({
  src,
  className = '',
}) => {
  const root = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const animationRef = useRef<any>(null);
  const hasAnimatedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!root.current || !videoRef.current) return;

    const video = videoRef.current;
    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting && videoRef.current) {
          if (animationRef.current) {
            animationRef.current.reset();
          }

          animationRef.current = animate(videoRef.current, {
            opacity: [0, 1],
            scale: [0.3, 1],
            duration: 500,
            easing: 'easeOutElastic(1, .3)',
          });

          videoRef.current.play();
          hasAnimatedRef.current = true;
        } else if (hasAnimatedRef.current && videoRef.current) {
          if (animationRef.current) {
            animationRef.current.reset();
          }

          animationRef.current = animate(videoRef.current, {
            opacity: [1, 0],
            scale: [1, 0.3],
            duration: 500,
            delay: 0,
            easing: 'easeInElastic(1, .3)',
          });

          videoRef.current.pause();
        }
      },
      {
        threshold: 0.7,
      }
    );

    observer.observe(root.current);

    return () => {
      observer.disconnect();
      if (animationRef.current) {
        animationRef.current.reset();
      }
    };
  }, [src]);

  return (
    <div ref={root} className={className}>
      <video ref={videoRef} src={src} muted loop playsInline preload='auto' />
    </div>
  );
};
